import { Link } from 'react-router-dom';
import { Users, Building2, IdCard, MapPin, UserCog, History, ShieldCheck } from 'lucide-react';
import { PageHeader } from '../../components/PageHeader';
import { useAuth } from '../../auth/AuthContext';

/**
 * Single entry point for system-level administration, separated from
 * People/HR (which is about managing staff as employees) - this page
 * is about configuring the platform itself: who has an account, what
 * roles exist, the org structure options staff records draw from,
 * and the audit trail. Same icon-tile hub pattern already used by
 * HR and Finance, so the whole app now has one consistent navigation
 * language instead of a deep header mega-menu.
 *
 * useAuth() is called exactly once, at the top level - permission
 * checks below read from that same Set via a plain function, never
 * by calling usePermission() (a hook) inside .filter(), which is a
 * genuine Rules-of-Hooks violation caught and fixed once already
 * this session on the TopNav component.
 */
export function SystemSetupHubPage() {
  const { permissions } = useAuth();
  const hasPermission = (p: string) => permissions.has(p);

  const cards = [
    { to: '/admin/access', icon: Users, title: 'People & Access', description: 'Manage user accounts, roles, and permission assignments.', permission: 'admin.users.view' },
    { to: '/admin/roles', icon: ShieldCheck, title: 'Roles & Permissions', description: 'Define roles and the permission catalogue they grant.', permission: 'admin.users.view' },
    { to: '/admin/departments', icon: Building2, title: 'Departments', description: 'Manage the organizational department structure.', permission: 'admin.departments.manage' },
    { to: '/admin/positions', icon: IdCard, title: 'Positions', description: 'Manage job positions and titles used across staff records.', permission: 'admin.positions.manage' },
    { to: '/admin/locations', icon: MapPin, title: 'Locations', description: 'Manage the country/state/LGA/ward location hierarchy.', permission: 'admin.locations.manage' },
    { to: '/admin/delegations', icon: UserCog, title: 'Delegations', description: 'Temporary authority delegation between staff members.', permission: 'admin.delegations.manage' },
    { to: '/admin/audit', icon: History, title: 'Audit Trail', description: 'Full system audit log of who changed what, and when.', permission: 'admin.audit.view' },
  ];

  return (
    <div className="page">
      <PageHeader title="System Setup" description="Platform configuration: accounts, roles, organizational structure, and the audit trail." />
      <div className="quick-actions-grid">
        {cards.filter((c) => hasPermission(c.permission)).map((c) => (
          <Link key={c.to} to={c.to} className="quick-action">
            <span className="quick-action-icon"><c.icon size={18} /></span>
            <div><strong>{c.title}</strong><div className="muted">{c.description}</div></div>
          </Link>
        ))}
      </div>
    </div>
  );
}
